function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from 'react';
import clsx from 'clsx';
import { DragDropProvider } from '@dnd-kit/react';
import { RestrictToVerticalAxis } from '@dnd-kit/abstract/modifiers';
import { move } from '@dnd-kit/helpers';
import _min from 'lodash/min';
import _max from 'lodash/max';
import _cloneDeep from 'lodash/cloneDeep';
import { MetricGroups, useExperimentsStore } from 'apps/experiments/src/store';
import { ExperimentMetricType } from 'apps/experiments/src/types';
import { useFlattenedMetricResults, useVariants } from 'apps/experiments/src/components/hooks/metric-results';
import { useExperimentMetrics } from 'iron/apps/experiments/src/components/hooks/metrics';
import { useExperimentGlobalFilters } from 'iron/apps/experiments/src/components/hooks/global-filters';
import { useStagedExperiment } from 'iron/apps/experiments/src/components/hooks/staged-experiment';
import styles from './index.module.styl';
import { ExperimentPageSummaryMetricsCategory } from './_category';
import { ExperimentPageSummaryMetricsFilterBar } from './_filter-bar';
import { ExperimentPageSummaryMetricsModal } from './_metric-modal';

/** Otherwise the lift is displayed as being from 0% to 0% on the chart, which is confusing. */
const ABS_MAX_MINIMUM = 0.01;
export function ExperimentPageSummaryMetrics() {
  var _draftMetricGroups$Ex, _draftMetricGroups$Ex2, _draftMetricGroups$Ex3;
  const {
    stagedExperiment: experiment
  } = useStagedExperiment();
  const metricResults = useExperimentsStore(state => state.metricResults);
  const metricsSearch = useExperimentsStore(state => state.metricsSearch);
  const {
    globalFilters
  } = useExperimentGlobalFilters();
  const {
    metrics,
    reorderMetrics
  } = useExperimentMetrics();
  const flattenedMetricResults = useFlattenedMetricResults();
  const variants = useVariants();
  const filteredVariants = globalFilters.variantsFilter ? [globalFilters.variantsFilter] : Array.from(variants);

  // this is used to compute the chart header/row possible range
  // we can't deal with infinite lifts or liftConfidence
  const processedMetricResults = React.useMemo(() => {
    let results = flattenedMetricResults.map(result => _objectSpread(_objectSpread({}, result), {}, {
      lift: Number.isFinite(result.lift) ? result.lift : 0,
      liftConfidence: Number.isFinite(result.liftConfidence) ? result.liftConfidence : 0
    }));

    // if we have a variants filter, remove all of those from the results
    if (globalFilters.variantsFilter) {
      results = results.filter(result => result.variant === globalFilters.variantsFilter);
    }
    return results;
  }, [flattenedMetricResults, globalFilters.variantsFilter]);
  const min = React.useMemo(() => Math.min(0, _min(processedMetricResults.map(result => result.lift - result.liftConfidence))!), [processedMetricResults]);
  const max = React.useMemo(() => Math.max(0, _max(processedMetricResults.map(result => result.lift + result.liftConfidence))!), [processedMetricResults]);
  const absMax = Math.max(ABS_MAX_MINIMUM, processedMetricResults.length ? Math.max(Math.abs(min), Math.abs(max)) * 1.1 : 0.5);
  const filteredMetrics = React.useMemo(() => metricsSearch ? metrics.filter(metric => metric.name.toLowerCase().includes(metricsSearch.toLowerCase())) : metrics, [metrics, metricsSearch]);

  // we need this map to contain keys for all possible metric types for drag and drop
  // to work correctly when moving a metric into an empty group
  const metricGroups = React.useMemo(() => filteredMetrics.reduce<MetricGroups>((groups, metric) => {
    groups[metric.type].push(metric);
    return groups;
  }, {
    [ExperimentMetricType.PRIMARY]: [],
    [ExperimentMetricType.GUARDRAIL]: [],
    [ExperimentMetricType.SECONDARY]: []
  }), [filteredMetrics]);

  // this draft state is used for drag and drop
  // if a drag is canceled, then we can easily revert to the original state
  const [draftMetricGroups, setDraftMetricGroups] = React.useState(_cloneDeep(metricGroups));
  React.useEffect(() => {
    setDraftMetricGroups(_cloneDeep(metricGroups));
  }, [metricGroups]);
  if (!experiment) {
    return null;
  }
  return <div className={clsx(styles.metrics, {
    [styles.multiVariant]: filteredVariants.length > 1
  })} data-sentry-component="ExperimentPageSummaryMetrics" data-sentry-source-file="index.tsx">
      <ExperimentPageSummaryMetricsFilterBar data-sentry-element="ExperimentPageSummaryMetricsFilterBar" data-sentry-source-file="index.tsx" />
      <DragDropProvider
    // @ts-ignore
    modifiers={[RestrictToVerticalAxis]} onDragOver={event => {
      setDraftMetricGroups(prev => move(prev, event));
    }} onDragEnd={event => {
      if (event.canceled) {
        setDraftMetricGroups(_cloneDeep(metricGroups));
        return;
      }
      reorderMetrics(draftMetricGroups);
    }} data-sentry-element="DragDropProvider" data-sentry-source-file="index.tsx">
        <ExperimentPageSummaryMetricsCategory absMax={absMax} header='experiments.main.metrics.primary' metrics={(_draftMetricGroups$Ex = draftMetricGroups[ExperimentMetricType.PRIMARY]) !== null && _draftMetricGroups$Ex !== void 0 ? _draftMetricGroups$Ex : []} variants={filteredVariants} type={ExperimentMetricType.PRIMARY} results={metricResults} data-sentry-element="ExperimentPageSummaryMetricsCategory" data-sentry-source-file="index.tsx" />
        <ExperimentPageSummaryMetricsCategory absMax={absMax} header='experiments.main.metrics.guardrail' metrics={(_draftMetricGroups$Ex2 = draftMetricGroups[ExperimentMetricType.GUARDRAIL]) !== null && _draftMetricGroups$Ex2 !== void 0 ? _draftMetricGroups$Ex2 : []} variants={filteredVariants} type={ExperimentMetricType.GUARDRAIL} results={metricResults} data-sentry-element="ExperimentPageSummaryMetricsCategory" data-sentry-source-file="index.tsx" />
        <ExperimentPageSummaryMetricsCategory absMax={absMax} header='experiments.main.metrics.secondary' metrics={(_draftMetricGroups$Ex3 = draftMetricGroups[ExperimentMetricType.SECONDARY]) !== null && _draftMetricGroups$Ex3 !== void 0 ? _draftMetricGroups$Ex3 : []} variants={filteredVariants} type={ExperimentMetricType.SECONDARY} results={metricResults} data-sentry-element="ExperimentPageSummaryMetricsCategory" data-sentry-source-file="index.tsx" />
      </DragDropProvider>
      <ExperimentPageSummaryMetricsModal data-sentry-element="ExperimentPageSummaryMetricsModal" data-sentry-source-file="index.tsx" />
    </div>;
}